import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../tokens/types';
import pdf from '../tokens/pdfStyles';
import { colors, fontFamily, space, typeScale } from '../tokens/designTokens';
import { PdfSection, PdfKvList, PdfChip, PdfSparkBars } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenKvList, ScreenChip, ScreenSparkBars } from '../primitives/ScreenPrimitives';
import { fmtDate } from './formatUtils';

const profileStatusTone = (s?: string): 'LOW' | 'MEDIUM' | 'HIGH' | 'muted' => {
    if (!s) return 'muted';
    const up = s.toUpperCase();
    if (up === 'COMPLETE') return 'LOW';
    if (up === 'PROCESSING' || up === 'IN_PROGRESS') return 'MEDIUM';
    if (up === 'LOCKED' || up === 'FAILED') return 'HIGH';
    return 'muted';
};
const trafficTone = (change?: string): 'LOW' | 'MEDIUM' | 'HIGH' => {
    const n = parseFloat((change || '0').replace('%', ''));
    if (!Number.isFinite(n)) return 'MEDIUM';
    return n >= 0 ? 'LOW' : 'HIGH';
};

const buildItems = (d: ReportData) => {
    const i = d.identity;
    return [
        { label: 'Legal name', value: i.orgName },
        { label: 'PAN', value: i.pan },
        { label: 'CIN', value: i.cin },
        { label: 'Sector', value: [i.sector, i.subSector].filter(Boolean).join(' · ') },
        { label: 'Headquarters', value: [i.state, i.fullAddress].filter(Boolean).join(' — ') },
        { label: 'Website', value: i.websiteUrl },
        { label: 'Contact', value: i.contactEmail },
        { label: 'Incorporated', value: fmtDate(i.foundationDate) },
        { label: 'Data last fetched', value: fmtDate(i.lastFetched) },
    ];
};

const PdfIdentity: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const items = buildItems(data);
    const gstins = data.identity.gstins ?? [];
    return (
        <PdfSection kicker="Entity snapshot" title="Identity & registration" index={index} breakBefore={breakBefore}>
            <View style={{ flexDirection: 'row', gap: space.lg }}>
                <View style={{ flex: 1 }}>
                    <PdfKvList items={items} />
                </View>
                <View style={{ width: 180 }}>
                    <Text
                        style={{
                            fontSize: typeScale.micro,
                            color: colors.muted,
                            letterSpacing: 1,
                            textTransform: 'uppercase',
                            marginBottom: 6,
                        }}
                    >
                        GSTINs ({gstins.length})
                    </Text>
                    {gstins.slice(0, 8).map((g) => (
                        // skill rule 9 corollary: sibling Texts with different weights — lineHeight:1 on both keeps baselines aligned
                        <View
                            key={g.gstin}
                            style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 3 }}
                        >
                            <Text style={{ fontSize: typeScale.caption, color: colors.muted, lineHeight: 1 }}>{g.state}</Text>
                            <Text
                                style={{
                                    fontSize: typeScale.caption,
                                    color: colors.ink,
                                    fontFamily: fontFamily.sansBold,
                                    lineHeight: 1,
                                }}
                            >
                                {g.gstin}
                            </Text>
                        </View>
                    ))}
                    {gstins.length > 8 ? (
                        <Text style={{ fontSize: typeScale.micro, color: colors.mutedSoft, marginTop: 4 }}>+{gstins.length - 8} more</Text>
                    ) : null}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: space.sm }}>
                        {data.identity.profileStatus ? (
                            <PdfChip tone={profileStatusTone(data.identity.profileStatus)}>{data.identity.profileStatus}</PdfChip>
                        ) : null}
                        {data.identity.startupSme ? <PdfChip tone="accent">{data.identity.startupSme}</PdfChip> : null}
                        {data.identity.gstConnected ? (
                            <PdfChip tone={data.identity.gstConnected === 'CONNECTED' ? 'LOW' : 'MEDIUM'}>
                                GST {data.identity.gstConnected}
                            </PdfChip>
                        ) : null}
                        {data.identity.companyAccessType ? <PdfChip tone="brand">{data.identity.companyAccessType}</PdfChip> : null}
                        {data.identity.aicaScore ? <PdfChip tone="info">AICA grade {data.identity.aicaScore}</PdfChip> : null}
                    </View>
                </View>
            </View>
            {data.identity.websiteTraffic ? (
                <View style={{ marginTop: space.md }} minPresenceAhead={80}>
                    <View style={[pdf.divider, { marginTop: 0, marginBottom: space.sm }]} />
                    <View
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 4,
                            gap: 8,
                        }}
                    >
                        {/* skill rule 9 corollary: title (h4 sans) paired with chip+label row — lineHeight:1 on every Text */}
                        <Text
                            style={{
                                fontFamily: fontFamily.body,
                                fontWeight: 600,
                                fontSize: typeScale.h4,
                                color: colors.ink00,
                                lineHeight: 1,
                            }}
                        >
                            Website traffic
                        </Text>
                        <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                            {data.identity.websiteTraffic.change ? (
                                <PdfChip tone={trafficTone(data.identity.websiteTraffic.change)}>
                                    {data.identity.websiteTraffic.change}
                                </PdfChip>
                            ) : null}
                            {data.identity.websiteTraffic.trendText ? (
                                <Text
                                    style={{
                                        fontSize: typeScale.micro,
                                        color: colors.ink40,
                                        letterSpacing: 0.3,
                                        textTransform: 'uppercase',
                                        lineHeight: 1,
                                    }}
                                >
                                    {data.identity.websiteTraffic.trendText}
                                </Text>
                            ) : null}
                        </View>
                    </View>
                    <PdfSparkBars values={data.identity.websiteTraffic.monthly.map((m) => m.traffic)} height={28} />
                    <Text style={{ fontSize: typeScale.micro, color: colors.ink40, marginTop: 3 }}>
                        Monthly unique visits · source: SimilarWeb
                    </Text>
                </View>
            ) : (
                <View style={[pdf.divider, { marginTop: space.md }]} />
            )}
        </PdfSection>
    );
};

const ScreenIdentity: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const items = buildItems(data);
    const gstins = data.identity.gstins ?? [];
    return (
        <ScreenSection kicker="Entity snapshot" title="Identity & registration" index={index}>
            <div style={{ display: 'flex', gap: 20 }}>
                <div style={{ flex: 1 }}>
                    <ScreenKvList items={items} />
                </div>
                <div style={{ width: 220 }}>
                    <div
                        style={{
                            fontSize: 11,
                            color: '#5B6370',
                            letterSpacing: 0.6,
                            textTransform: 'uppercase',
                            marginBottom: 8,
                        }}
                    >
                        GSTINs ({gstins.length})
                    </div>
                    {gstins.slice(0, 8).map((g) => (
                        <div key={g.gstin} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12 }}>
                            <span style={{ color: '#5B6370' }}>{g.state}</span>
                            <span style={{ fontFamily: 'Gilroy-Bold' }}>{g.gstin}</span>
                        </div>
                    ))}
                    {gstins.length > 8 ? (
                        <div style={{ fontSize: 11, color: '#8A8F99', marginTop: 4 }}>+{gstins.length - 8} more</div>
                    ) : null}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {data.identity.profileStatus ? (
                            <ScreenChip tone={profileStatusTone(data.identity.profileStatus)}>{data.identity.profileStatus}</ScreenChip>
                        ) : null}
                        {data.identity.startupSme ? <ScreenChip tone="accent">{data.identity.startupSme}</ScreenChip> : null}
                        {data.identity.gstConnected ? (
                            <ScreenChip tone={data.identity.gstConnected === 'CONNECTED' ? 'LOW' : 'MEDIUM'}>
                                GST {data.identity.gstConnected}
                            </ScreenChip>
                        ) : null}
                        {data.identity.companyAccessType ? <ScreenChip tone="brand">{data.identity.companyAccessType}</ScreenChip> : null}
                        {data.identity.aicaScore ? <ScreenChip tone="info">AICA grade {data.identity.aicaScore}</ScreenChip> : null}
                    </div>
                </div>
            </div>
            {data.identity.websiteTraffic ? (
                <div style={{ marginTop: 16, borderTop: `1px solid ${colors.rule}`, paddingTop: 12 }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 6,
                            gap: 10,
                            flexWrap: 'wrap',
                        }}
                    >
                        <div style={{ fontSize: 13, fontWeight: 600, color: colors.ink00 }}>Website traffic</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {data.identity.websiteTraffic.change ? (
                                <ScreenChip tone={trafficTone(data.identity.websiteTraffic.change)}>
                                    {data.identity.websiteTraffic.change}
                                </ScreenChip>
                            ) : null}
                            {data.identity.websiteTraffic.trendText ? (
                                <span
                                    style={{
                                        fontSize: 10,
                                        color: colors.ink40,
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {data.identity.websiteTraffic.trendText}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <ScreenSparkBars values={data.identity.websiteTraffic.monthly.map((m) => m.traffic)} height={32} />
                    <div style={{ fontSize: 11, color: colors.ink40, marginTop: 4 }}>Monthly unique visits · source: SimilarWeb</div>
                </div>
            ) : null}
        </ScreenSection>
    );
};

export const identityModule: ModuleDefinition = {
    meta: {
        id: 'identity',
        title: 'Identity & registration',
        description: 'Basic KYC, registration & access status.',
        dataSource: 'Assessee Basic, Profile Overview',
    },
    hasData: (d) => !!d.identity?.orgName,
    Pdf: PdfIdentity,
    Screen: ScreenIdentity,
};
